type SketchToolbarProps = {
  color: string;
  width: number;
  colors: string[];
  minWidth?: number;
  maxWidth?: number;
  settingsLabel: string;
  clearLabel: string;
  colorLabel: string;
  widthLabel: string;
  onChangeColor: (color: string) => void;
  onChangeWidth: (width: number) => void;
  onClear: () => void;
};

export default function SketchToolbar({
  color,
  width,
  colors,
  minWidth = 2,
  maxWidth = 12,
  settingsLabel,
  clearLabel,
  colorLabel,
  widthLabel,
  onChangeColor,
  onChangeWidth,
  onClear,
}: SketchToolbarProps) {
  return (
    <>
      <div className="mb-2 flex flex-shrink-0 items-center justify-between">
        <p className="text-xs font-medium text-foreground">{settingsLabel}</p>
        <button
          type="button"
          onClick={onClear}
          className="flex cursor-pointer items-center gap-1.5 text-xs text-destructive transition-colors hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
        >
          <i className="ri-eraser-line" aria-hidden="true" />
          <span>{clearLabel}</span>
        </button>
      </div>
      <div className="mb-2 flex-shrink-0 rounded-xl bg-muted p-2">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{colorLabel}</span>
        </div>
        <div role="radiogroup" aria-label={colorLabel} className="flex gap-2">
          {colors.map((c) => {
            const checked = color === c;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                onClick={() => onChangeColor(c)}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                  e.preventDefault();
                  const idx = colors.indexOf(color);
                  const next =
                    e.key === "ArrowRight"
                      ? (idx + 1) % colors.length
                      : (idx - 1 + colors.length) % colors.length;
                  onChangeColor(colors[next]);
                }}
                className={`h-6 w-6 cursor-pointer rounded-full border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  checked
                    ? "scale-110 border-foreground shadow-lg"
                    : "border-border hover:border-muted-foreground"
                }`}
                style={{ backgroundColor: c }}
                aria-label={`${colorLabel}: ${c}`}
              />
            );
          })}
        </div>
      </div>
      <div className="mb-2 flex-shrink-0 rounded-xl bg-muted p-2">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{widthLabel}</span>
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
            {width}px
          </span>
        </div>
        <input
          type="range"
          min={minWidth}
          max={maxWidth}
          value={width}
          aria-label={widthLabel}
          aria-valuetext={`${width}px`}
          onChange={(e) => onChangeWidth(Number(e.target.value))}
          className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-muted-foreground/20 accent-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
    </>
  );
}
